import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useLayoutEffect, useState, type CSSProperties } from "react";

// useLayoutEffect roda antes do 1º paint no cliente (sem flash); no SSR vira useEffect (sem warning).
const useIsoLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect;
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { User, Lock, Eye, EyeOff, ShieldCheck, BarChart3, Clock, Loader2 } from "lucide-react";
// Import normal de asset do Vite (bundlado no build, mesma origem, sem
// round-trip de rede) — NUNCA o antigo `gps-drive-logo-v3.png.asset.json`
// (`{ url: "/__l5e/assets-v1/..." }`). Esse `.asset.json` é resolvido por um
// proxy de DEV do @lovable.dev/vite-tanstack-config (`apply: "serve"`,
// preview.lovable.dev) — não existe em produção, então `/__l5e/...` sempre
// dá 404 fora do preview do Lovable. Bug real 2026-09-10: "logo aparece
// quebrada na primeira abertura, digitar conserta" — no 1º acesso (sem
// cache de branding em localStorage ainda), a logo cai neste fallback ANTES
// de GET /public/login-branding responder; com o `.asset.json` isso
// significava <img> quebrada por alguns instantes (o "conserta ao digitar"
// era só a resposta da branding chegando por perto, sem relação causal com
// a tecla em si — ver efectiveBranding abaixo). Trocando para um import
// normal, o fallback já é uma imagem válida (a logo real da empresa, não um
// placeholder) desde o primeiro paint, então a troca pra branding
// (quando/se vier diferente) nunca aparece como "quebrada→certa".
import defaultLogoUrl from "@/assets/gps-drive-logo-v3.png";
import { useAuth } from "@/contexts/auth-context";
import { ApiError } from "@/lib/api/client";
import { LoginBackgroundLayer } from "@/components/login-screen-mockup";
import { DEFAULT_LOGIN_BACKGROUND_THEME } from "@/lib/login-background-themes";
import { usePublicLoginBranding } from "@/hooks/queries";
import { applyBrandIcon, applyBrandTitle } from "@/hooks/use-branding-theme";
import { GpsDriveApiError, resolveGpsDriveUrl, WRONG_COMPANY_DOMAIN } from "@/lib/api/gpsdrive-client";
import { LOGIN_NOTICE_KEY } from "@/contexts/auth-context";
import { getTechnicianToken, technicianAppService } from "@/services/technician-app.service";
import type { PublicLoginBrandingSettings } from "@/services/gpsdrive-admin-branding.service";
import { cn } from "@/lib/utils";

// Quanto o login espera a identidade visual (logo/fundo) antes de aparecer
// com o padrão/cache — nunca mais que isso, mesmo se a API não responder.
const BRANDING_WAIT_MS = 1200;

// Fundos do login do endereço da casa (gpsdrive.com.br), desenhados junto
// com a animação dos veículos (login-map-motion.tsx).
const HOUSE_LOGIN_ART = ["f03c3f95-c13d-4360-98c7-d68b48ddc4d7", "b78bbb47-8a8a-45bd-81ab-af75422b6206"];
function isHouseLoginArt(url: string | null | undefined): boolean {
  return !!url && HOUSE_LOGIN_ART.some((k) => url.includes(k));
}

export const Route = createFileRoute("/login")({
  head: () => ({
    meta: [
      { title: "Entrar — GPS Drive" },
      { name: "description", content: "Acesse sua conta GPS Drive para monitorar sua frota em tempo real." },
    ],
  }),
  component: LoginPage,
});

// Cor/tema padrão enquanto GET /public/login-branding ainda não respondeu
// (ou falhou) — nunca deixa a tela em branco esperando a rede.
const DEFAULT_ACCENT_COLOR = "#2563eb";

// Última resposta de /public/login-branding conhecida, para reaproveitar
// como fallback ENQUANTO a busca desta visita ainda não voltou — sem isso,
// a tela ficava totalmente em branco (nada além de bg-white, nenhum
// formulário) até a chamada responder, e se ela travar/demorar (rede
// instável, backend momentaneamente lento logo após o logout invalidar
// tokens/cookies), o usuário via a tela travada em branco indefinidamente,
// só recuperando com F5 (bug real relatado 2026-09-01: "cliquei em sair e
// ficou a tela toda branca"). Guardar a última resposta boa em
// localStorage deixa o formulário aparecer na hora (com o tema/cor da
// última vez), sem esperar rede nenhuma — a chamada ainda roda em segundo
// plano e só troca visualmente se o valor realmente mudou.
const LOGIN_BRANDING_CACHE_KEY = "gpsdrive:login-branding-cache";

function readCachedLoginBranding(): PublicLoginBrandingSettings | null {
  try {
    const raw = localStorage.getItem(LOGIN_BRANDING_CACHE_KEY);
    return raw ? (JSON.parse(raw) as PublicLoginBrandingSettings) : null;
  } catch {
    return null;
  }
}

// "Esqueceu sua senha?" removido em 2026-11-03: abria o WhatsApp fixo da WP
// em todas as empresas (inclusive Politracker). Sem o botão em nenhuma tela.

function LoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Técnico já logado (mesmo login, visual do app do técnico) — vai direto
  // pra /tecnico; sessão vencida lá volta pra cá já sem o token.
  useEffect(() => {
    if (getTechnicianToken()) navigate({ to: "/tecnico", replace: true });
  }, [navigate]);

  // Saída forçada (sessão aberta no domínio de outra empresa) — ver
  // AuthProvider.
  useEffect(() => {
    try {
      const notice = sessionStorage.getItem(LOGIN_NOTICE_KEY);
      if (notice) {
        sessionStorage.removeItem(LOGIN_NOTICE_KEY);
        setError(notice);
      }
    } catch {
      // sem sessionStorage
    }
  }, []);

  // Fundo/logo configurados em Administração > Identidade Visual > Tela de
  // Login — endpoint público (sem autenticação, roda antes do login).
  // Enquanto o sistema for de fato single-tenant (uma empresa só cadastrada,
  // ver AdminBrandingRepository#findPrimaryCompanyId) isso reflete direto a
  // escolha feita lá; se um dia houver mais de uma empresa, sempre mostra a
  // mais antiga até existir um jeito de identificar a empresa antes do
  // login. `data` fica `undefined` até responder OU se a chamada falhar —
  // os `??` abaixo garantem que a tela nunca fica quebrada por causa disso.
  const { data: branding, isError: brandingError } = usePublicLoginBranding();
  // Cache lido só no cliente, em layout effect (antes do 1º paint). Ler no
  // useState inicial dava HTML do servidor (tema padrão) != cliente e o
  // usuário via a tela "antiga" antes da nova.
  const [mounted, setMounted] = useState(false);
  const [cachedBranding, setCachedBranding] = useState<PublicLoginBrandingSettings | null>(null);
  useEffect(() => {
    if (!branding) return;
    // Ícone da aba/PWA do endereço acessado já no login (empresa sem
    // favicon → ícone neutro, nunca o da GPS Drive).
    if (branding.house !== undefined) {
      applyBrandIcon(branding.faviconUrl, branding.house, undefined, branding.appIconUrl);
      applyBrandTitle(branding.brandName, branding.house);
    }
    try {
      localStorage.setItem(LOGIN_BRANDING_CACHE_KEY, JSON.stringify(branding));
    } catch {
      // localStorage indisponível (modo privado/quota) — sem cache, só
      // perde o fallback instantâneo na próxima visita, nunca quebra a tela.
    }
  }, [branding]);
  const effectiveBranding = branding ?? cachedBranding;
  const backgroundTheme = effectiveBranding?.loginBackgroundTheme ?? DEFAULT_LOGIN_BACKGROUND_THEME;
  const backgroundColor = effectiveBranding?.loginBackgroundColor ?? DEFAULT_ACCENT_COLOR;
  // Raio em volta do card (pedido 2026-10-29): cor principal da empresa;
  // antes do backend mandar, a última cor salva neste navegador.
  // Lido no efeito (não no render) pra não divergir do HTML do SSR.
  const [cachedBrandColor, setCachedBrandColor] = useState<string | null>(null);
  useEffect(() => {
    try {
      setCachedBrandColor(localStorage.getItem("gpsdrive:branding-color"));
    } catch {
      // armazenamento bloqueado — fica a cor padrão
    }
  }, []);
  const glowColor = effectiveBranding?.primaryColor ?? cachedBrandColor ?? DEFAULT_ACCENT_COLOR;
  // Imagem de fundo por dispositivo (Identidade Visual): app instalado/celular
  // usa a "PWA", tablet a "Tablet", o resto a de computador — cada variante
  // sem imagem cai na de computador. Decidido no cliente (useEffect) pra não
  // dar hydration mismatch em SSR.
  const [device, setDevice] = useState<"desktop" | "tablet" | "pwa">("desktop");
  useIsoLayoutEffect(() => {
    setCachedBranding(readCachedLoginBranding());
    setMounted(true);
    const detect = () => {
      const standalone =
        window.matchMedia("(display-mode: standalone)").matches ||
        (navigator as Navigator & { standalone?: boolean }).standalone === true ||
        // @capacitor/core cria window.Capacitor também no navegador comum
        // (plataforma "web") — só o app nativo conta como PWA/app.
        (window as Window & { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor?.isNativePlatform?.() === true;
      const w = window.innerWidth;
      setDevice(standalone || w < 600 ? "pwa" : w <= 1024 ? "tablet" : "desktop");
    };
    detect();
    window.addEventListener("resize", detect);
    return () => window.removeEventListener("resize", detect);
  }, []);
  const rawBackgroundImage =
    (device === "pwa" && effectiveBranding?.loginBackgroundPwaImageUrl) ||
    (device === "tablet" && effectiveBranding?.loginBackgroundTabletImageUrl) ||
    effectiveBranding?.loginBackgroundImageUrl ||
    null;
  const backgroundImageUrl = rawBackgroundImage ? resolveGpsDriveUrl(rawBackgroundImage) : null;
  // Logo padrão da GPS Drive só no endereço da casa — empresa sem logo
  // nenhum fica sem imagem (o servidor já cai no logotipo dela).
  const loginLogoUrl = effectiveBranding?.loginLogoUrl
    ? resolveGpsDriveUrl(effectiveBranding.loginLogoUrl)
    : effectiveBranding?.house === false
      ? null
      : defaultLogoUrl;
  const loginLogoSize = effectiveBranding?.loginLogoSize ?? null;

  // Nunca mostra a tela "antiga"/padrão nem imagem carregando aos poucos:
  // só desenha depois de saber o branding (cache local ou resposta) E de a
  // imagem de fundo estar baixada; até lá, fundo neutro escuro liso.
  const [loadedImage, setLoadedImage] = useState<string | null>(null);
  useEffect(() => {
    if (!backgroundImageUrl) return;
    let alive = true;
    const done = () => alive && setLoadedImage(backgroundImageUrl);
    const img = new Image();
    img.onload = done;
    img.onerror = done;
    img.src = backgroundImageUrl;
    const t = setTimeout(done, 15000);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [backgroundImageUrl]);
  // Tela preta relatada por clientes (web/PWA/app) em 2026-09-24: antes o
  // formulário inteiro ficava escondido atrás de um fundo escuro liso até
  // a imagem de fundo baixar (PNG de ~2 MB no computador, até 4 s) — e
  // para SEMPRE se GET /public/login-branding travasse sem responder.
  // Agora: espera o branding no máximo BRANDING_WAIT_MS e nunca espera a
  // imagem — o formulário aparece e a imagem entra com fade quando chegar.
  const [brandingWaitOver, setBrandingWaitOver] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setBrandingWaitOver(true), BRANDING_WAIT_MS);
    return () => clearTimeout(t);
  }, []);
  const imageReady = !backgroundImageUrl || loadedImage === backgroundImageUrl;
  const ready = mounted && (!!effectiveBranding || brandingError || brandingWaitOver);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await login(email, password);
      // replace: true — sem isso, a entrada "/login" fica presa no
      // histórico do WebView (Capacitor/Android). Apertar a seta de voltar
      // do sistema navegava de volta pra cá e renderizava a tela de login de
      // novo (sessão no servidor continuava válida, mas parecia "deslogado"
      // pro cliente). Todo outro redirect do app (__root.tsx AuthGate,
      // app-sidebar.tsx, mobile-app.tsx, master.login.tsx) já usa
      // replace: true — este era o único caso fora do padrão.
      navigate({ to: "/", replace: true });
    } catch (err) {
      // Mesmo login para o técnico da Agenda (pedido 2026-10-08): se não é
      // uma conta do rastreamento, tenta o usuário/senha de Agenda >
      // Técnicos e abre o visual do app do técnico (/tecnico).
      if (err instanceof ApiError && err.status === 401) {
        try {
          await technicianAppService.login(email.trim(), password);
          navigate({ to: "/tecnico", replace: true });
          return;
        } catch {
          // não é técnico — segue com a mensagem normal
        }
      }
      setError(
        err instanceof GpsDriveApiError && err.code === WRONG_COMPANY_DOMAIN
          ? err.message
          : err instanceof ApiError && err.status === 401
            ? "Login ou senha inválidos."
            : "Não foi possível entrar. Tente novamente.",
      );
    } finally {
      setLoading(false);
    }
  };

  // Enquanto GET /public/login-branding não respondeu, usa o cache local
  // da última resposta boa (effectiveBranding acima) em vez de travar a
  // tela em branco — antes disso a tela ficava totalmente vazia até a
  // chamada responder (bug 2026-09-01, ver comentário do cache acima).
  // Sem cache (primeira visita do navegador), cai no DEFAULT_* declarado
  // acima; o "pisca" default→correto (sintoma original 2026-08-28) só
  // volta a acontecer nesse caso raro, nunca mais em toda visita.
  //
  // "Instala o app e fica tudo escuro" (2026-09-28): na 1ª abertura nada
  // está em cache — a espera acima era uma tela azul-escura vazia e, depois
  // dela, o fundo fixo #0b1220 ficava à mostra até a imagem da empresa
  // baixar (no celular o painel de login também é escuro, então a tela
  // inteira ficava escura por alguns segundos). Agora a espera é branca com
  // logo+spinner (continua a splash branca do app e o "Carregando…") e o
  // fundo base usa a cor da própria identidade visual, com branco como padrão.
  const baseBackgroundColor = effectiveBranding?.loginBackgroundColor ?? "#ffffff";
  if (!ready) {
    return (
      <div className="fixed inset-0 flex flex-col items-center justify-center gap-4 bg-white">
        {loginLogoUrl && <img src={loginLogoUrl} alt="" className="h-20 w-auto animate-pulse object-contain" />}
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div
      className="relative flex min-h-screen w-full items-center justify-center overflow-hidden max-sm:fixed max-sm:inset-0 max-sm:min-h-0"
      style={{ backgroundColor: baseBackgroundColor }}
    >
      {imageReady && (
        <div className="absolute inset-0 animate-in fade-in duration-700">
          <LoginBackgroundLayer
            theme={backgroundTheme}
            color={backgroundColor}
            imageUrl={backgroundImageUrl}
            motion={
              // Carrinhos animados só nas artes ORIGINAIS do GPS Drive (as
              // ruas delas batem com o trajeto da animação). Imagem enviada
              // por uma empresa fica limpa — pedido 2026-10-27 (WP: "os
              // carrinhos animados em cima, nada a ver").
              !isHouseLoginArt(rawBackgroundImage)
                ? null
                : rawBackgroundImage === effectiveBranding?.loginBackgroundPwaImageUrl
                  ? "pwa"
                  : "desktop"
            }
          />
        </div>
      )}

      <div className="pointer-events-none absolute inset-0 z-10 bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.18)_0%,rgba(255,255,255,0.05)_32%,transparent_58%)]" />

      <div className="relative z-20 w-full max-w-md px-6 max-sm:static max-sm:max-w-none max-sm:p-0">
        {/* Celular (<640px): painel vira Bottom Sheet fixo na base, mapa/imagem de
            fundo fica visível acima. Desktop/tablet mantém o card centralizado. */}
        <div
          style={{ "--lg-c": glowColor } as CSSProperties}
          className="login-glow rounded-2xl border border-white/40 bg-white/60 p-8 shadow-2xl shadow-slate-900/10 backdrop-blur-xl max-sm:fixed max-sm:inset-x-0 max-sm:bottom-0 max-sm:z-30 max-sm:max-h-[52dvh] max-sm:overflow-y-auto max-sm:rounded-b-none max-sm:rounded-t-3xl max-sm:border-[rgba(80,150,220,0.25)] max-sm:bg-[rgba(8,20,35,0.72)] max-sm:px-5 max-sm:pb-[max(1rem,env(safe-area-inset-bottom))] max-sm:pt-0 max-sm:shadow-[0_-10px_40px_rgba(0,0,0,0.35)] max-sm:backdrop-blur-[20px]">
          <div className="mx-auto mb-3 mt-2.5 hidden h-[5px] w-[45px] rounded-[10px] bg-slate-400/40 max-sm:block" />
          <div className="mb-6 flex flex-col items-center max-sm:mb-2">
            {loginLogoUrl && <img
              src={loginLogoUrl}
              alt={effectiveBranding?.brandName ?? ""}
              className={cn(
                "w-auto object-contain max-sm:max-h-10",
                !loginLogoSize && "h-24",
                // Pulsa só enquanto nem a busca desta visita nem o cache local
                // (ver readCachedLoginBranding acima) resolveram ainda — 1ª
                // visita do navegador/app sem nada salvo, único caso em que a
                // logo real da empresa ainda não é conhecida (pedido
                // 2026-09-09: "ficar piscando como se tivesse carregando").
                !effectiveBranding && "animate-pulse",
              )}
              style={loginLogoSize ? { height: loginLogoSize } : undefined}
            />}
            <h1 className="mt-2 text-2xl font-bold text-slate-900 max-sm:mt-1 max-sm:text-lg max-sm:text-white">Bem-vindo de volta</h1>
            <p className="mt-1 text-sm text-slate-500 max-sm:hidden">Acesse sua central de rastreamento</p>
            
          </div>

          <form onSubmit={handleSubmit} className="space-y-4 max-sm:space-y-2.5">
            <div>
              <label className="mb-1.5 block text-xs font-medium text-slate-700 max-sm:hidden">Usuário, e-mail ou CPF</label>
              <div className="relative">
                <User className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                {/* type="text" de propósito — nunca "email": login também
                    aceita CPF/CNPJ ou nome de usuário como identificador
                    (ver Email/Login em users.tsx), e o type="email" nativo
                    do browser rejeitava qualquer valor sem "@" antes mesmo
                    de chegar no submit (bug relatado 2026-08-31: cliente
                    cadastrado com CPF como login não conseguia entrar). */}
                <Input type="text" autoCapitalize="none" autoCorrect="off" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Usuário, e-mail ou CPF" className="h-11 pl-9 max-sm:h-10 max-sm:border-sky-400/25 max-sm:bg-white/5 max-sm:text-white max-sm:placeholder:text-slate-400" />
              </div>
            </div>

            <div>
              <label className="mb-1.5 block text-xs font-medium text-slate-700 max-sm:hidden">Senha</label>
              <div className="relative">
                <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input type={showPass ? "text" : "password"} required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Sua senha" className="h-11 pl-9 pr-10 max-sm:h-10 max-sm:border-sky-400/25 max-sm:bg-white/5 max-sm:text-white max-sm:placeholder:text-slate-400" />
                <button type="button" onClick={() => setShowPass((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" aria-label={showPass ? "Ocultar senha" : "Mostrar senha"}>
                  {showPass ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div className="flex items-center text-xs max-sm:hidden">
              <label className="flex items-center gap-2 text-slate-600">
                <input type="checkbox" className="h-3.5 w-3.5 rounded border-slate-300" />
                Lembrar de mim
              </label>
            </div>

            {error && (
              <p role="alert" className="text-sm font-medium text-red-600">
                {error}
              </p>
            )}

            <Button type="submit" disabled={loading} className="h-11 w-full bg-sky-600 text-white hover:bg-sky-700 max-sm:h-10 max-sm:bg-[#008CFF] max-sm:hover:bg-[#0079e0]">
              {loading ? "Entrando..." : "Entrar →"}
            </Button>
          </form>

          <p className="mt-6 text-center text-xs text-slate-500 max-sm:hidden">
            © {new Date().getFullYear()} GPS Drive · Rastreamento em tempo real
          </p>

          <div className="mt-3 hidden max-sm:block">
            <div className="flex justify-around text-[11px] text-slate-300">
              <span className="flex items-center gap-1"><ShieldCheck className="h-3.5 w-3.5 text-[#008CFF]" />Mais segurança</span>
              <span className="flex items-center gap-1"><BarChart3 className="h-3.5 w-3.5 text-[#008CFF]" />Mais controle</span>
              <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5 text-[#008CFF]" />Em tempo real</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
